import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { useToast } from '../lib/Toast';
import { humanizeError } from '../lib/humanizeError';
import {
  fmtMoney, fmtMoney0, fmtDate, fmtDateTime, statusPill,
  CUSTOMER_TYPES, CURRENCIES, ROUTES, TERMS, CUSTOMER_STATUSES,
} from './b2bLib';
import '../po/drawer.css';

export default function CustomerDrawer({ customerId, customers, invoices, session, fx, onClose, onOpenInvoice, onNewInvoice, onChanged }) {
  const toast = useToast();
  const roll = useMemo(() => customers.find(c => c.customer_id === customerId) || null, [customers, customerId]);
  const myInvoices = useMemo(() => invoices.filter(i => i.customer_id === customerId)
    .sort((a, b) => (b.issue_date || '').localeCompare(a.issue_date || '')), [invoices, customerId]);

  const [full, setFull] = useState(null);   // raw b2b_customers row (editable)
  const [activity, setActivity] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    const ids = myInvoices.map(i => i.id);
    const [c, a] = await Promise.all([
      aws.from('b2b_customers').select('*').eq('id', customerId),
      ids.length ? aws.from('b2b_activity').select('*').in('invoice_id', ids).order('occurred_at', { ascending: false }) : Promise.resolve({ data: [] }),
    ]);
    const row = (c.data || [])[0] || null;
    setFull(row);
    setActivity(a.data || []);
    if (row) setD({
      name: row.name || '', type: row.type, country: row.country || '', currency: row.currency,
      abn: row.abn || '', route_to_market: row.route_to_market, default_terms: row.default_terms,
      status: row.status, pricing_tier: row.pricing_tier || '',
      contacts: Array.isArray(row.contacts) ? row.contacts : [],
      delivery_addresses: Array.isArray(row.delivery_addresses) ? row.delivery_addresses : [],
      notes: row.notes || '',
    });
  }, [customerId, myInvoices]);
  useEffect(() => { load(); }, [load]);

  async function run(fn, okMsg) {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); onChanged && onChanged(); }
    catch (e) { const { title, body } = humanizeError(e, 'Save failed'); toast.error(title, body); }
    finally { setBusy(false); }
  }

  const save = () => run(async () => {
    await authWrite(session, 'b2b_customers', 'update', {
      name: d.name, type: d.type, country: d.country || null, currency: d.currency, abn: d.abn || null,
      route_to_market: d.route_to_market, default_terms: d.default_terms, status: d.status,
      pricing_tier: d.pricing_tier || null, contacts: d.contacts, delivery_addresses: d.delivery_addresses,
      notes: d.notes || null, updated_at: new Date().toISOString(),
    }, { column: 'id', value: customerId });
    setEditing(false);
  }, 'Customer saved');

  if (!roll) return null;
  const cur = roll.currency;

  return createPortal(
    <>
      <div onClick={() => !busy && onClose()} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(3px)' }} />
      <aside style={{ position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 201, width: 600, maxWidth: '96vw', background: '#fff', boxShadow: '-12px 0 48px rgba(0,0,0,0.16)', display: 'flex', flexDirection: 'column' }}>
        {/* header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e4e4e7', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0a0a0a' }}>{roll.name}</div>
            <div style={{ fontSize: 12, color: '#a1a1aa', marginTop: 2 }}>{roll.type} · {roll.country || '—'} · {cur}{roll.route_to_market !== 'Direct' ? ` · ${roll.route_to_market}` : ''}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#a1a1aa' }}>×</button>
        </div>

        {/* rollup stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, padding: '12px 22px', borderBottom: '1px solid #f4f4f5' }}>
          <Stat label="Lifetime" value={fmtMoney0(roll.lifetime_spend, cur)} />
          <Stat label="Outstanding" value={fmtMoney0(roll.current_outstanding, cur)} accent={roll.current_outstanding > 0 ? '#A56B00' : '#16a34a'} />
          <Stat label="Avg pay" value={roll.avg_days_to_pay != null ? `${roll.avg_days_to_pay}d` : '—'} />
          <Stat label="Overdue" value={roll.overdue_count || 0} accent={roll.overdue_count ? '#dc2626' : undefined} />
        </div>

        <div style={{ display: 'flex', gap: 6, padding: '10px 22px', borderBottom: '1px solid #f4f4f5' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setEditing(v => !v)}>{editing ? 'Close edit' : 'Edit customer'}</button>
          <button className="btn btn-primary btn-sm" onClick={() => onNewInvoice(customerId)}>+ New invoice</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>
          {editing && d && (
            <div style={{ background: '#fafafa', borderRadius: 10, padding: 14, marginBottom: 18 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <FG label="Name"><input className="form-input" value={d.name} onChange={e => setD(s => ({ ...s, name: e.target.value }))} /></FG>
                <FG label="Type"><select className="form-select" value={d.type} onChange={e => setD(s => ({ ...s, type: e.target.value }))}>{CUSTOMER_TYPES.map(t => <option key={t}>{t}</option>)}</select></FG>
                <FG label="Region / country"><input className="form-input" value={d.country} onChange={e => setD(s => ({ ...s, country: e.target.value }))} /></FG>
                <FG label="Currency"><select className="form-select" value={d.currency} onChange={e => setD(s => ({ ...s, currency: e.target.value }))}>{CURRENCIES.map(c => <option key={c}>{c}</option>)}</select></FG>
                <FG label="Route to market"><select className="form-select" value={d.route_to_market} onChange={e => setD(s => ({ ...s, route_to_market: e.target.value }))}>{ROUTES.map(r => <option key={r}>{r}</option>)}</select></FG>
                <FG label="Default terms"><select className="form-select" value={d.default_terms} onChange={e => setD(s => ({ ...s, default_terms: e.target.value }))}>{TERMS.map(t => <option key={t}>{t}</option>)}</select></FG>
                <FG label="Status"><select className="form-select" value={d.status} onChange={e => setD(s => ({ ...s, status: e.target.value }))}>{CUSTOMER_STATUSES.map(s2 => <option key={s2}>{s2}</option>)}</select></FG>
                <FG label="ABN"><input className="form-input" value={d.abn} onChange={e => setD(s => ({ ...s, abn: e.target.value }))} /></FG>
                <FG label="Pricing tier"><input className="form-input" value={d.pricing_tier} onChange={e => setD(s => ({ ...s, pricing_tier: e.target.value }))} /></FG>
              </div>

              <SubList title="Contacts" items={d.contacts} fields={['name', 'email', 'phone']}
                onChange={(items) => setD(s => ({ ...s, contacts: items }))} />
              <SubList title="Delivery addresses" items={d.delivery_addresses} fields={['label', 'address']}
                onChange={(items) => setD(s => ({ ...s, delivery_addresses: items }))} />

              <FG label="Notes"><textarea className="form-textarea" value={d.notes} onChange={e => setD(s => ({ ...s, notes: e.target.value }))} rows={2} /></FG>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
                <button className="btn btn-primary btn-sm" disabled={busy} onClick={save}>Save customer</button>
              </div>
            </div>
          )}

          {/* contacts read view */}
          {!editing && Array.isArray(full?.contacts) && full.contacts.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Contacts</div>
              {full.contacts.map((c, i) => <div key={i} style={{ fontSize: 12, color: '#52525b' }}>{[c.name, c.email, c.phone].filter(Boolean).join(' · ') || '—'}</div>)}
            </div>
          )}

          {/* invoices */}
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Invoices ({myInvoices.length})</div>
          <div style={{ marginBottom: 18 }}>
            {myInvoices.map(i => (
              <div key={i.id} onClick={() => onOpenInvoice(i.id)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #fafafa', cursor: 'pointer' }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#0a0a0a' }}>{i.invoice_ref || i.po_ref || '—'}</div>
                  <div style={{ fontSize: 10, color: '#a1a1aa' }}>{fmtDate(i.issue_date)} · due {fmtDate(i.due_date)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(i.balance, i.currency)}</span>
                  <Pill p={statusPill(i.status, i.is_overdue)} />
                </div>
              </div>
            ))}
            {myInvoices.length === 0 && <div style={{ fontSize: 11, color: '#a1a1aa' }}>No invoices.</div>}
          </div>

          {/* activity timeline */}
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Activity timeline</div>
          {activity.length === 0 ? <div style={{ fontSize: 11, color: '#a1a1aa' }}>No activity.</div> :
            activity.map(a => (
              <div key={a.id} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid #fafafa' }}>
                <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: '#f4f4f5', color: '#52525b', height: 'fit-content', textTransform: 'uppercase' }}>{a.type}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: '#0a0a0a' }}>{a.body}</div>
                  <div style={{ fontSize: 10, color: '#a1a1aa' }}>{a.actor || 'system'} · {fmtDateTime(a.occurred_at)}</div>
                </div>
              </div>
            ))}
        </div>
      </aside>
    </>,
    document.body,
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: accent || '#0F1419', marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}
function FG({ label, children }) { return <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label" style={{ fontSize: 11 }}>{label}</label>{children}</div>; }
function Pill({ p }) { return <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: p.bg, color: p.fg }}>{p.label}</span>; }

function SubList({ title, items, fields, onChange }) {
  const upd = (i, f, v) => { const next = items.map((x, j) => j === i ? { ...x, [f]: v } : x); onChange(next); };
  const add = () => onChange([...items, Object.fromEntries(fields.map(f => [f, '']))]);
  const rm = (i) => onChange(items.filter((_, j) => j !== i));
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#52525b' }}>{title}</span>
        <button onClick={add} style={{ fontSize: 11, background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer' }}>+ add</button>
      </div>
      {items.map((it, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 5 }}>
          {fields.map(f => (
            <input key={f} placeholder={f} value={it[f] || ''} onChange={e => upd(i, f, e.target.value)}
              style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #e4e4e7', borderRadius: 6, flex: 1 }} />
          ))}
          <button onClick={() => rm(i)} style={{ background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer' }}>×</button>
        </div>
      ))}
    </div>
  );
}
